import { Link } from "react-router-dom"

import type { SolarShareReadinessDto } from "~/api/dto/solar-presentation.dto"
import {
  usePublishSolarShare,
  useSolarShareState,
  useSolarShareViews,
  useUnpublishSolarShare,
} from "~/api/hooks/useSolarPresentationShare"
import { solarShareBlockedReason } from "~/api/solar-presentation-errors"
import type {
  NotReadyReadiness,
  ShareKindBundle,
} from "~/containers/ProposalSetup/share/share-kind-bundle"
import { ShareDrawer } from "~/containers/ProposalSetup/share/ShareDrawer"

import {
  solarBlockerCopy,
  SOLAR_NOT_READY_PROMPT,
  SOLAR_PUBLISHED_STALE_PROMPT,
} from "./solar-share-copy"
import { solarSetupGroupPath } from "../solar-setup-paths"
import { solarShareFixPath } from "../solar-urls"

type SolarNotReady = NotReadyReadiness<SolarShareReadinessDto>

/**
 * Why sharing is unavailable, one row per blocker with the link that clears
 * it. Beside an active link it explains what the homeowner currently sees.
 */
function SolarNotReady({
  proposalId,
  readiness,
  published,
}: {
  proposalId: string
  readiness: SolarNotReady
  published: boolean
}): JSX.Element {
  return (
    <>
      <p role="status" className="text-[length:var(--step--1)] text-bp-gold">
        {published ? SOLAR_PUBLISHED_STALE_PROMPT : SOLAR_NOT_READY_PROMPT}
      </p>
      {readiness.blockers.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {readiness.blockers.map((blocker) => (
            <li
              key={blocker.code}
              data-testid={`solar-share-blocker-${blocker.code}`}
              className="flex flex-col items-start gap-1"
            >
              <span className="text-[length:var(--step--2)] text-bp-text-muted">
                {solarBlockerCopy(blocker.code, blocker.step).text}
              </span>
              <Link
                to={solarShareFixPath(proposalId, blocker.step)}
                className="text-[length:var(--step--2)] font-semibold text-bp-accent underline underline-offset-2 hover:text-bp-text"
              >
                {solarBlockerCopy(blocker.code, blocker.step).fixLabel}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  )
}

export const solarShareBundle: ShareKindBundle<SolarShareReadinessDto> = {
  testIdPrefix: `solar`,
  useShareState: useSolarShareState,
  useShareViews: useSolarShareViews,
  usePublish: usePublishSolarShare,
  useUnpublish: useUnpublishSolarShare,
  // The refusal carries only its reason; the readiness re-read it triggers
  // brings the blockers.
  blockedReadiness: (error) => {
    const reason = solarShareBlockedReason(error)
    return reason
      ? {
          ready: false,
          reason,
          pricingStatus: `out_of_date`,
          savingsStatus: `out_of_date`,
          blockers: [],
        }
      : null
  },
  // CASL decides Solar updates, and the shell opens this drawer only for an
  // actor whose header read says `viewerCanUpdate`.
  updatePermissions: null,
  NotReady: SolarNotReady,
  // Closing returns to the rail's Share group, which stays open beside it.
  returnTo: (proposalId) => solarSetupGroupPath(proposalId, `share`),
}

/** Routed sibling of the Setup drawers at /solar-proposal/:proposalId/share. */
export const SolarShareDrawer = (): JSX.Element | null => (
  <ShareDrawer bundle={solarShareBundle} />
)
